/**
 * One place for the console's login data.
 *
 * The login used to live in sessionStorage, so closing the tab or opening the
 * console in a new tab signed you out. It now lives in localStorage (as the
 * CRM and HRMS already do through @platform/auth-sdk), so it survives reloads
 * and is shared across tabs.
 *
 * Reads fall back to sessionStorage once and move the value across, so
 * anyone signed in before this change stays signed in.
 *
 * An access token whose JWT `exp` has passed is treated as missing, so a
 * stale login never shows a half-working console.
 */

const KEYS = ['access_token', 'refresh_token', 'user_role', 'platform_role', 'admin_console_user'];

const safe = (fn, fallback = null) => {
  try {
    return fn();
  } catch {
    return fallback;
  }
};

export const authGet = (key) => {
  const fromLocal = safe(() => localStorage.getItem(key));
  if (fromLocal !== null) return fromLocal;

  // One-time move from the old sessionStorage location.
  const fromSession = safe(() => sessionStorage.getItem(key));
  if (fromSession !== null) {
    safe(() => localStorage.setItem(key, fromSession));
    safe(() => sessionStorage.removeItem(key));
  }
  return fromSession;
};

export const authSet = (key, value) => {
  if (value === undefined || value === null) {
    authRemove(key);
    return;
  }
  safe(() => localStorage.setItem(key, String(value)));
};

export const authRemove = (key) => {
  safe(() => localStorage.removeItem(key));
  safe(() => sessionStorage.removeItem(key));
};

export const clearAuth = () => KEYS.forEach(authRemove);

/** Seconds since epoch at which the JWT expires, or null if unknown. */
export const tokenExpiry = (token) => {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  return safe(() => {
    const json = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    const exp = JSON.parse(json).exp;
    return typeof exp === 'number' ? exp : null;
  });
};

/** The access token, or null when absent or expired (expired ones are cleared). */
export const getAccessToken = () => {
  const token = authGet('access_token');
  if (!token) return null;
  const exp = tokenExpiry(token);
  if (exp !== null && exp * 1000 <= Date.now()) {
    clearAuth();
    return null;
  }
  return token;
};

export const getStoredUser = () => {
  const raw = authGet('admin_console_user');
  if (!raw) return null;
  return safe(() => JSON.parse(raw));
};

/**
 * Called on a 401 from the Identity Center: the session is over.
 * Clears the login and sends the browser to the sign-in screen.
 */
export const endSession = () => {
  clearAuth();
  safe(() => localStorage.setItem('logout', Date.now().toString()));
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/auth') && window.location.pathname !== '/') {
    window.location.href = '/';
  }
};
